/**
 * Workflow Canvas API client — /shay/email/workflow-config.
 * Ref: 04_Frontend_Canvas_Rule_To_Workflow_Changes.md §5, 05_Frontend_API_Changes_2026-07-15.md §1
 *
 * All endpoints require the user's own session JWT (the same token already held after
 * login) — the backend derives company_id from it. The separate "ML API key" the
 * orchestrator used to accept is no longer valid on any of these routes.
 */
import { API_CONFIG, WORKFLOW_CONFIG_API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';
import type { WorkflowConfigDocument, WorkflowVersionSummary } from '../components/ChannelSettings/WorkflowCanvas/workflowGraphTypes';
import { parseErrorDetail } from './apiErrorUtils';

export type WorkflowBackend = 'generic' | 'leave';

/** Reserved config names — these are now real path segments (worker-only endpoints), never assign a config this name (ref: changelog §3). */
export const RESERVED_WORKFLOW_NAMES = ['agent', 'auto-trigger'] as const;

/** Reserved name prefix — used internally for inline run-test persistence (ref: 06_...md §6). */
export const RESERVED_WORKFLOW_NAME_PREFIX = '__runtest__';

export function isReservedWorkflowName(name: string): boolean {
  const trimmed = name.trim().toLowerCase();
  if ((RESERVED_WORKFLOW_NAMES as readonly string[]).includes(trimmed)) return true;
  return trimmed.startsWith(RESERVED_WORKFLOW_NAME_PREFIX);
}

export interface ValidateWorkflowRequest {
  nodes: WorkflowConfigDocument['nodes'];
  edges: WorkflowConfigDocument['edges'];
  created_by: string;
  backend: WorkflowBackend;
  dry_run: boolean;
}

export interface ValidateWorkflowResponse {
  valid: boolean;
  errors: string[];
}

/** A single unmet capability gap in a draft (ref: 06_Frontend_API_Changes_Action_Catalog_2026-07-20.md §5). */
export interface DraftCapabilityMissing {
  tier: number;
  node_id: string;
  action_id?: string;
  message: string;
}

/** NEW — always present going forward; only `null` for pre-catalog drafts (ref: §5). */
export interface DraftCapability {
  tier: number;
  can_generate: boolean;
  missing: DraftCapabilityMissing[];
}

export interface DraftWorkflowResponse {
  name: string;
  nodes: WorkflowConfigDocument['nodes'];
  edges: WorkflowConfigDocument['edges'];
  raw_llm_notes: string;
  /** NEW — surface `missing[].message` prominently for tier 3 (ref: §5). */
  capability?: DraftCapability | null;
}

export interface RunTestRequest {
  company_id: string;
  sender_email: string;
  email_text: string;
  employee_id?: string;
  employee_name?: string;
  leave_type?: string;
  start_date?: string;
  end_date?: string;
  days_requested?: number;
  extra_context?: Record<string, unknown>;
  /** Inline unsaved graph — tests the in-progress canvas state before it's ever saved (ref: spec §3 step 6). */
  nodes?: WorkflowConfigDocument['nodes'];
  edges?: WorkflowConfigDocument['edges'];
  dry_run?: boolean;
}

export interface RunTestResponse {
  /** Opaque — now shaped generic::{company_id}::{name}::{ref_id}. Never parse or reconstruct it (ref: changelog §2). */
  workflow_id: string;
  ref_id: string;
}

export interface RunTestCurrentNodeResponse {
  current_node_id: string | null;
}

export interface RunTestStatusResponse {
  status: string;
  result?: unknown;
}

export interface SignalRequest {
  signal_name: string;
  payload: Record<string, unknown>;
}

/**
 * One row per distinct workflow name the company has ever saved (latest version's summary
 * each). Company-wide, not per-user. Excludes internal `__runtest__` rows and an unsaved
 * "leave" seed/demo template — never try to add those back client-side
 * (ref: Workflow_Save_API_Integration.md §6).
 */
export interface WorkflowNameSummary {
  name: string;
  version: number;
  status: string;
  created_by: string;
  created_at: string;
}


function getSessionToken(): string {
  return (
    localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
    localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
    ''
  );
}

function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    accept: 'application/json',
    Authorization: `Bearer ${getSessionToken()}`,
  };
}

/** GET /{name} → latest saved version, scoped to your company via the session JWT. */
export async function getWorkflowConfig(name: string): Promise<WorkflowConfigDocument> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.GET_CONFIG(name)}`,
    { method: 'GET', headers: authHeaders() }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/**
 * GET /names — one row per distinct workflow name the company has ever saved (latest version's
 * summary each), sorted most-recently-updated first. Company-wide, not per-user. This is the
 * "which workflow do you want to open" list — separate from GET /{name}/versions, which only
 * makes sense once a name is already known (ref: Workflow_Save_API_Integration.md §6).
 */
export async function getWorkflowNames(): Promise<WorkflowNameSummary[]> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.NAMES}`,
    { method: 'GET', headers: authHeaders() }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/** GET /{name}/versions, scoped to your company via the session JWT. */
export async function getWorkflowVersions(name: string): Promise<WorkflowVersionSummary[]> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.GET_VERSIONS(name)}`,
    { method: 'GET', headers: authHeaders() }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/**
 * GET /{name}/versions/{version} — the exact same WorkflowConfigDocument shape as GET /{name},
 * but for one specific past version instead of always the latest. 404s if that exact version
 * was never saved under this name for your company (ref: Workflow Canvas — Version History
 * "Load" Fix, 2026-07-24).
 */
export async function getWorkflowConfigVersion(name: string, version: number): Promise<WorkflowConfigDocument> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.GET_VERSION(name, version)}`,
    { method: 'GET', headers: authHeaders() }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/** POST /{name}/validate — structural checks only (not "does this template_id exist"). */
export async function validateWorkflow(
  name: string,
  payload: ValidateWorkflowRequest
): Promise<ValidateWorkflowResponse> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.VALIDATE(name)}`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/** POST /{name} — append-only, always creates a new version. Server re-validates and 422s if invalid. */
export async function saveWorkflowConfig(
  name: string,
  payload: ValidateWorkflowRequest
): Promise<WorkflowConfigDocument> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.SAVE(name)}`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/** POST /{name}/draft — LLM designs the entire graph from a plain-language description. Nothing is persisted. */
export async function draftWorkflow(
  name: string,
  description: string
): Promise<DraftWorkflowResponse> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.DRAFT(name)}`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ description }) }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/** POST /{name}/run-test — starts a live Temporal-backed test execution. */
export async function startRunTest(
  name: string,
  payload: RunTestRequest
): Promise<RunTestResponse> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.RUN_TEST(name)}`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/** GET /run-test/{workflowId}/current-node — poll target for canvas highlight. */
export async function getRunTestCurrentNode(workflowId: string): Promise<RunTestCurrentNodeResponse> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.RUN_TEST_CURRENT_NODE(workflowId)}`,
    { method: 'GET', headers: authHeaders() }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/** GET /run-test/{workflowId}/status — poll target for the traffic-light indicator. */
export async function getRunTestStatus(workflowId: string): Promise<RunTestStatusResponse> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.RUN_TEST_STATUS(workflowId)}`,
    { method: 'GET', headers: authHeaders() }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/**
 * POST /run-test/{workflowId}/signal — responds to a paused WAIT_SIGNAL.
 * Caller must look up the real signal_name from the WAIT_SIGNAL node the workflow is
 * currently parked on (current_node_id from the poll) — sending the wrong name returns
 * 200 OK but never unblocks the workflow. 404s if workflow_id doesn't belong to your company.
 */
export async function sendRunTestSignal(
  workflowId: string,
  payload: SignalRequest
): Promise<void> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${WORKFLOW_CONFIG_API_ENDPOINTS.RUN_TEST_SIGNAL(workflowId)}`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
}
